var React = require('react');
var { createElement: h, useRef, useEffect } = React;

// Run Log.
//   Collapsed: a small bottom strip showing the latest live log line.
//   Expanded (click): a full-height right-side panel listing every run-log
//                     line, auto-scrolled to the newest. Completed/failed
//                     lines also show their input + output (truncated) inline.
//
// Pure presentational — all state (lines, open/closed) is owned by the parent.
// Props:
//   lines:       [{ id, tone, text, nodeId?, durationMs?, input?, output? }]
//   open:        bool
//   onToggle:         () => void
//   onHoverLine:      (nodeId) => void   focus the canvas on the node on hover
//   onHoverLineLeave: () => void         restore the canvas view on hover-out

var MAX_IO_CHARS = 2000;

// Pretty-print a value to a string, truncated so a huge PDF/base64 payload
// can't blow up the panel. Returns '' for undefined (so we render nothing).
function formatIoValue(value) {
  if (value === undefined) return '';
  var text;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch (e) {
    text = String(value);
  }
  if (text == null) return '';
  if (text.length > MAX_IO_CHARS) {
    return text.slice(0, MAX_IO_CHARS) + '\n… (truncated)';
  }
  return text;
}

// One labeled input/output block (scrollable, truncated). Renders null when
// there's nothing to show.
function ioBlock(label, value) {
  var text = formatIoValue(value);
  if (!text) return null;
  return h('div', { className: 'run-log-io' },
    h('span', { className: 'run-log-io-label' }, label),
    h('pre', { className: 'run-log-io-value' }, text)
  );
}

function RunLogPanel(props) {
  var lines = Array.isArray(props.lines) ? props.lines : [];
  var open = !!props.open;
  var onToggle = props.onToggle;
  var onHoverLine = props.onHoverLine;
  var onHoverLineLeave = props.onHoverLineLeave;
  var listRef = useRef(null);

  // Auto-scroll to the newest line whenever a line is added (or on expand).
  useEffect(function() {
    if (!open) return;
    var el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length, open]);

  // Nothing to show until at least one line exists.
  if (lines.length === 0) return null;

  var latest = lines[lines.length - 1];

  // ── Expanded: full right-side drawer ──
  if (open) {
    return h('div', { className: 'run-log-drawer' },
      h('div', { className: 'run-log-drawer-header' },
        h('span', { className: 'run-log-strip-label' }, 'Run log'),
        h('span', { className: 'run-log-strip-count' }, lines.length + ' lines'),
        h('button', {
          type: 'button',
          className: 'run-log-close-btn',
          title: 'Hide run log',
          onClick: function(e) { e.stopPropagation(); if (onToggle) onToggle(); }
        },
          h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' },
            h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
            h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
          )
        )
      ),
      h('div', { className: 'run-log-list', ref: listRef },
        lines.map(function(line) {
          var hasIo = line.input !== undefined || line.output !== undefined;
          return h('div', {
            key: line.id,
            className: 'run-log-line-wrap' + (line.nodeId ? ' run-log-line-wrap-focusable' : ''),
            onMouseEnter: line.nodeId && onHoverLine ? function() { onHoverLine(line.nodeId); } : undefined,
            onMouseLeave: line.nodeId && onHoverLineLeave ? function() { onHoverLineLeave(); } : undefined
          },
            h('div', { className: 'run-log-line' },
              h('span', { className: 'run-log-dot run-log-tone-' + (line.tone || 'info') }),
              h('span', { className: 'run-log-line-text' }, line.text)
            ),
            hasIo
              ? h('div', { className: 'run-log-io-group' },
                  ioBlock('Input', line.input),
                  ioBlock('Output', line.output)
                )
              : null
          );
        })
      )
    );
  }

  // ── Collapsed: small bottom strip with the latest live line ──
  return h('div', { className: 'run-log-panel' },
    h('button', {
      type: 'button',
      className: 'run-log-strip',
      onClick: function(e) { e.stopPropagation(); if (onToggle) onToggle(); },
      title: 'Show run log'
    },
      h('span', { className: 'run-log-chevron' },
        h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('polyline', { points: '6 9 12 15 18 9' })
        )
      ),
      h('span', { className: 'run-log-strip-label' }, 'Run log'),
      h('span', { className: 'run-log-strip-latest run-log-tone-' + (latest.tone || 'info') }, latest.text)
    )
  );
}

module.exports = RunLogPanel;
